import React from 'react';
import Icon from '../../../components/AppIcon';
import CollapsiblePanel from './CollapsiblePanel';
import ComponentAnimationPreview from './ComponentAnimationPreview';
import {
  ANIMATION_EASINGS,
  ANIMATION_PRESETS,
  ANIMATION_TIMING_MODES,
  ANIMATION_TRIGGERS,
  DEFAULT_COMPONENT_ANIMATION,
  DEFAULT_SECTION_ANIMATION,
  normalizeComponentAnimation,
  normalizeSectionAnimation,
  patchComponentAnimation,
  patchSectionAnimation,
} from '../utils/componentAnimation';
import { getComponentDisplayName } from '../utils/defaults';

const inputClass =
  'w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring';

function Field({ label, hint, children }) {
  return (
    <label className="block space-y-1.5">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      {children}
      {hint ? <p className="text-[11px] text-muted-foreground/80 leading-snug">{hint}</p> : null}
    </label>
  );
}

function RangeField({ label, value, min, max, step = 1, unit = '', onChange, hint }) {
  return (
    <Field label={`${label}${unit ? ` (${value}${unit})` : ''}`} hint={hint}>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full accent-primary"
      />
      <div className="flex justify-between text-[10px] text-muted-foreground">
        <span>
          {min}
          {unit}
        </span>
        <span>
          {max}
          {unit}
        </span>
      </div>
    </Field>
  );
}

export default function ComponentAnimationFields({
  animation,
  componentType,
  componentId,
  sectionComponents = [],
  sectionAnimation,
  mode = 'component',
  hidePreview = false,
  onChange,
}) {
  const config =
    mode === 'section'
      ? normalizeSectionAnimation(animation)
      : normalizeComponentAnimation(animation);
  const patch = (next) =>
    onChange(
      mode === 'section'
        ? patchSectionAnimation(animation, next)
        : patchComponentAnimation(animation, next),
    );
  const sectionAnim = normalizeSectionAnimation(sectionAnimation);
  const sectionEnabled = sectionAnim.enabled;
  const siblingOptions = (sectionComponents || []).filter((c) => c.id !== componentId);
  const showOwnTrigger =
    config.timing === 'solo' || (config.timing === 'after' && !sectionEnabled);
  const showInheritedTrigger =
    mode === 'component' &&
    !showOwnTrigger &&
    (config.timing === 'section' || config.timing === 'after') &&
    sectionEnabled;

  return (
    <div className="flex flex-col lg:flex-row gap-5 h-full min-h-0">
      <div className="flex-1 min-w-0 space-y-5 overflow-y-auto pr-1 max-h-[min(62vh,560px)]">
      <label className="flex items-center justify-between gap-3 rounded-lg border border-border bg-card px-3 py-2.5 cursor-pointer">
        <div>
          <p className="text-sm font-medium text-foreground">
            {mode === 'section' ? 'Activer l’animation de section' : 'Activer l’animation'}
          </p>
          <p className="text-xs text-muted-foreground">
            {mode === 'section'
              ? 'Anime toute la section et peut orchestrer les composants.'
              : 'Appliquée sur la page publiée et dans l’aperçu.'}
          </p>
        </div>
        <input
          type="checkbox"
          className="h-4 w-4 accent-primary"
          checked={config.enabled}
          onChange={(e) => patch({ enabled: e.target.checked })}
        />
      </label>

      {config.enabled ? (
        <>
          {mode === 'component' ? (
            <CollapsiblePanel title="Ordre d’apparition" defaultOpen>
              <div className="space-y-3 pt-1">
                <Field label="Mode de synchronisation">
                  <div className="space-y-2">
                    {ANIMATION_TIMING_MODES.map((timing) => {
                      const selected = config.timing === timing.id;
                      return (
                        <label
                          key={timing.id}
                          className={`flex items-start gap-3 rounded-lg border px-3 py-2.5 cursor-pointer transition-smooth ${
                            selected
                              ? 'border-primary bg-primary/5'
                              : 'border-border hover:border-primary/30'
                          }`}
                        >
                          <input
                            type="radio"
                            name="animation-timing"
                            className="mt-0.5 accent-primary"
                            checked={selected}
                            onChange={() => patch({ timing: timing.id })}
                          />
                          <span>
                            <span className="text-sm font-medium text-foreground block">
                              {timing.label}
                            </span>
                            <span className="text-xs text-muted-foreground">{timing.description}</span>
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </Field>

                {config.timing === 'after' ? (
                  siblingOptions.length ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <Field label="Après le composant">
                        <select
                          className={inputClass}
                          value={config.afterComponentId || ''}
                          onChange={(e) => patch({ afterComponentId: e.target.value })}
                        >
                          <option value="">— Choisir un composant —</option>
                          {siblingOptions.map((c) => (
                            <option key={c.id} value={c.id}>
                              {getComponentDisplayName(c)}
                            </option>
                          ))}
                        </select>
                      </Field>
                      <RangeField
                        label="Délai après"
                        value={config.delayAfterMs}
                        min={0}
                        max={2000}
                        step={10}
                        unit=" ms"
                        hint="Ex. 10 ms après le composant choisi."
                        onChange={(delayAfterMs) => patch({ delayAfterMs })}
                      />
                    </div>
                  ) : (
                    <p className="text-xs text-amber-700 dark:text-amber-400 bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2">
                      Ajoutez au moins un autre composant dans cette section pour enchaîner les
                      animations.
                    </p>
                  )
                ) : null}

                {config.timing === 'section' ? (
                  sectionEnabled ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <Field label="Ordre dans la séquence" hint="0 = ordre automatique (grille).">
                        <input
                          type="number"
                          min={0}
                          max={99}
                          className={inputClass}
                          value={config.sectionOrder ?? 0}
                          onChange={(e) => patch({ sectionOrder: Number(e.target.value) || 0 })}
                        />
                      </Field>
                      <RangeField
                        label="Délai supplémentaire"
                        value={config.delay}
                        min={0}
                        max={2000}
                        step={10}
                        unit=" ms"
                        onChange={(delay) => patch({ delay })}
                      />
                    </div>
                  ) : (
                    <p className="text-xs text-amber-700 dark:text-amber-400 bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2">
                      Activez l’animation sur la section (double-clic section → Animation) pour
                      utiliser le mode « Avec la section ».
                    </p>
                  )
                ) : null}
              </div>
            </CollapsiblePanel>
          ) : null}

          <Field label="Type d’animation">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-52 overflow-y-auto pr-1">
              {ANIMATION_PRESETS.map((preset) => {
                const selected = config.preset === preset.id;
                return (
                  <button
                    key={preset.id}
                    type="button"
                    onClick={() => patch({ preset: preset.id })}
                    className={`flex items-center gap-2 rounded-lg border px-2.5 py-2 text-left text-xs transition-smooth ${
                      selected
                        ? 'border-primary bg-primary/10 text-primary font-medium'
                        : 'border-border hover:border-primary/40 hover:bg-muted/40 text-foreground'
                    }`}
                  >
                    <Icon name={preset.icon} size={14} className="shrink-0" />
                    <span className="truncate">{preset.label}</span>
                  </button>
                );
              })}
            </div>
          </Field>

          {showOwnTrigger || mode === 'section' ? (
            <Field label={mode === 'section' ? 'Déclenchement de la section' : 'Déclenchement'}>
              <select
                className={inputClass}
                value={config.trigger}
                onChange={(e) => patch({ trigger: e.target.value })}
              >
                {ANIMATION_TRIGGERS.map((trigger) => (
                  <option key={trigger.id} value={trigger.id}>
                    {trigger.label}
                  </option>
                ))}
              </select>
              <p className="text-[11px] text-muted-foreground/80 mt-1">
                {ANIMATION_TRIGGERS.find((t) => t.id === config.trigger)?.description}
              </p>
            </Field>
          ) : showInheritedTrigger ? (
            <p className="text-xs text-muted-foreground bg-muted/20 rounded-lg px-3 py-2">
              Déclenchement hérité de la section (
              {ANIMATION_TRIGGERS.find((t) => t.id === sectionAnim.trigger)?.label || 'scroll'}
              ).
            </p>
          ) : null}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <RangeField
              label="Durée"
              value={config.duration}
              min={100}
              max={3000}
              step={50}
              unit=" ms"
              onChange={(duration) => patch({ duration })}
            />
            {showOwnTrigger || mode === 'section' ? (
              <RangeField
                label="Délai"
                value={config.delay}
                min={0}
                max={2000}
                step={50}
                unit=" ms"
                onChange={(delay) => patch({ delay })}
              />
            ) : null}
          </div>

          <Field label="Courbe d’animation">
            <select
              className={inputClass}
              value={config.easing}
              onChange={(e) => patch({ easing: e.target.value })}
            >
              {ANIMATION_EASINGS.map((ease) => (
                <option key={ease.id} value={ease.id}>
                  {ease.label}
                </option>
              ))}
            </select>
          </Field>

          {(mode === 'section' || showOwnTrigger) &&
          (config.trigger === 'scroll' || config.trigger === 'scrollOut') ? (
            <CollapsiblePanel title="Réglages scroll" defaultOpen>
              <div className="space-y-4 pt-1">
                <RangeField
                  label="Seuil de visibilité"
                  value={Math.round(config.scrollThreshold * 100)}
                  min={0}
                  max={100}
                  step={5}
                  unit=" %"
                  hint={
                    mode === 'section'
                      ? 'Part de la section visible avant le déclenchement.'
                      : 'Part du composant visible avant le déclenchement.'
                  }
                  onChange={(pct) => patch({ scrollThreshold: pct / 100 })}
                />
                <RangeField
                  label="Marge scroll"
                  value={config.scrollOffset}
                  min={-200}
                  max={200}
                  step={10}
                  unit=" px"
                  onChange={(scrollOffset) => patch({ scrollOffset })}
                />
                {config.trigger === 'scroll' ? (
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      className="h-4 w-4 accent-primary"
                      checked={config.scrollOnce}
                      onChange={(e) => patch({ scrollOnce: e.target.checked })}
                    />
                    <span>Jouer une seule fois</span>
                  </label>
                ) : null}
              </div>
            </CollapsiblePanel>
          ) : null}

          {config.trigger === 'scrollContinuous' ? (
            <CollapsiblePanel title="Parallaxe" defaultOpen>
              <div className="space-y-4 pt-1">
                <RangeField
                  label="Intensité"
                  value={Math.round(config.parallaxSpeed * 100)}
                  min={5}
                  max={200}
                  step={5}
                  unit=" %"
                  onChange={(pct) => patch({ parallaxSpeed: pct / 100 })}
                />
                <Field label="Direction">
                  <select
                    className={inputClass}
                    value={config.parallaxDirection}
                    onChange={(e) => patch({ parallaxDirection: e.target.value })}
                  >
                    <option value="up">Vers le haut au scroll</option>
                    <option value="down">Vers le bas au scroll</option>
                  </select>
                </Field>
              </div>
            </CollapsiblePanel>
          ) : null}

          {config.trigger !== 'scrollContinuous' ? (
            <CollapsiblePanel title="Paramètres avancés" defaultOpen={false}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-1">
                <RangeField
                  label="Distance"
                  value={config.distance}
                  min={0}
                  max={200}
                  step={4}
                  unit=" px"
                  onChange={(distance) => patch({ distance })}
                />
                <RangeField
                  label="Échelle initiale"
                  value={Math.round(config.scale * 100)}
                  min={10}
                  max={150}
                  step={1}
                  unit=" %"
                  onChange={(pct) => patch({ scale: pct / 100 })}
                />
                <RangeField
                  label="Rotation"
                  value={config.rotate}
                  min={0}
                  max={180}
                  step={1}
                  unit="°"
                  onChange={(rotate) => patch({ rotate })}
                />
                <RangeField
                  label="Flou initial"
                  value={config.blur}
                  min={0}
                  max={24}
                  step={1}
                  unit=" px"
                  onChange={(blur) => patch({ blur })}
                />
              </div>
            </CollapsiblePanel>
          ) : null}

          <button
            type="button"
            className="text-xs text-muted-foreground hover:text-foreground underline-offset-2 hover:underline"
            onClick={() =>
              onChange(mode === 'section' ? { ...DEFAULT_SECTION_ANIMATION } : { ...DEFAULT_COMPONENT_ANIMATION })
            }
          >
            Réinitialiser l’animation
          </button>
        </>
      ) : null}
      </div>

      {!hidePreview ? (
        <div className="lg:w-[300px] shrink-0 lg:sticky lg:top-0 lg:self-start">
          <ComponentAnimationPreview animation={config} componentType={componentType} tall />
        </div>
      ) : null}
    </div>
  );
}
